import { buildQuery } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { IconCalendarPlus } from '@calendly/ui/components/glyphs/IconCalendarPlus'
import { BareItem } from '@calendly/ui/components/menu'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { useContext } from 'react'

import { hasAnswerChoices } from 'components/event_type_edit'
import { useTrack } from 'hooks/analytics_events'

import { MeetingDetailDependenciesContext } from '../meeting_detail_context'

export const BookFollowUpMeetingButton = ({ event, inDropdown = false }) => {
  const dependencies = useContext(MeetingDetailDependenciesContext)
  if (!dependencies) {
    throw new Error('BookFollowUpMeetingButton must be wrapped in a MeetingDetailDependenciesProvider')
  }
  const { getScheduleFollowUpAnalyticsPayload, getScheduleInRealTimeAnalyticsPayload } = dependencies
  const eventType = event.eventType
  const track = useTrack()

  if (
    eventType?.deleted
    || eventType?.type === 'AdhocEventType'
    || event.modified
    || eventType?.kind !== 'solo'
    || (event.kind && event.kind !== 'CALENDLY')
  ) {
    return null
  }

  const invitee = event.invitee

  const buildOutboundPhoneNumber = () => {
    if (event.locationType === 'outbound_call') {
      return event.location
    }
    return ''
  }

  const findAndUpdateAnswerPosition = (customField) => {
    const foundAnswer = invitee.eventFields.find((eventField) => eventField.name === customField.name)
    return {
      ...foundAnswer,
      position: customField.position
    }
  }

  const findAnswerIndexes = (answerChoices, answer) => answer.values.map((value) => {
    const index = answerChoices?.findIndex((choice) => choice === value)
    return index < 0 ? answerChoices.length + 1 : index + 1
  })

  const formatAnswer = (customField, answer) => {
    const hasMultipleChoices = hasAnswerChoices(customField.format)

    if (!answer.values) {
      return ''
    }

    if (hasMultipleChoices && answer.values.length) {
      const answerIndexes = findAnswerIndexes(customField.answer_choices, answer)
      return answerIndexes
    }

    return answer.values
  }

  const buildPredefinedAnswers = () => {
    let predefinedAnswersQueryString = ''

    if (!eventType?.customFields?.length) {
      return predefinedAnswersQueryString
    }

    eventType.customFields.map((customField, i) => {
      if (!customField.enabled) {
        return predefinedAnswersQueryString
      }

      const answer = findAndUpdateAnswerPosition(customField)
      const formattedAnswer = encodeURIComponent(formatAnswer(customField, answer))
      predefinedAnswersQueryString += `&a${i + 1}=${formattedAnswer}`
      return predefinedAnswersQueryString
    })

    return predefinedAnswersQueryString
  }

  const buildHref = () => {
    const {
      name,
      email,
      guestEmails,
      phoneNumber
    } = invitee
    const query = buildQuery({
      name,
      email,
      guests: guestEmails?.toString(),
      location: buildOutboundPhoneNumber(),
      phone_number: phoneNumber
    })
    return `${eventType.bookingUrl}?${query}&${buildPredefinedAnswers()}`
  }

  const handleClick = () => {
    const scheduleInRealTimePayload = getScheduleInRealTimeAnalyticsPayload()
    const scheduleFollowUpPayload = getScheduleFollowUpAnalyticsPayload(event)
    track(scheduleInRealTimePayload)
    track(scheduleFollowUpPayload)
  }

  if (inDropdown) {
    return (
      <BareItem
        as="a"
        href={buildHref()}
        onClick={handleClick}
        target="_blank"
        rel="opener"
      >
        <UiIcon icon={IconCalendarPlus} size="small" />
        {I18n.t('publisher.meetings.book_follow_up')}
      </BareItem>
    )
  }

  return (
    <Button
      as="a"
      href={buildHref()}
      onClick={handleClick}
      target="_blank"
      rel="opener"
      size="small"
      decoration="secondary-outline"
    >
      <UiIcon icon={IconCalendarPlus} size="small" />
      {I18n.t('publisher.meetings.book_follow_up')}
    </Button>
  )
}
